// councilProtocol.js — Council v2 protocol normalization (PURE, framework-free).
//
// The v2 wire contract carries the phase as a NAMED STRING in the SAME `phase`
// field that v1 used for a number (SPEC: `phase:'opinions'|'review'|'rebuttal'|
// 'synthesis'`). Legacy v1 callers keep numeric 1/2/3. Terminal outcomes are a
// status independent of phase. Server persistence/streaming must never leak raw
// upstream strings to the UI, and unknown cost must never render as free $0.
//
// This module is the single pure seam normalizing those shapes so the client,
// hook and UI agree — and so the rules are testable without React or fetch.

/** Canonical v2 phase order. `rebuttal` sits between review and synthesis. */
export const COUNCIL_PHASES = ['opinions', 'review', 'rebuttal', 'synthesis'];

/** Terminal outcomes — orthogonal to phase (a run can end mid-review as partial). */
export const TERMINAL_STATUSES = ['complete', 'partial', 'cancelled', 'error'];

// Legacy numeric→name (v1). 3 is ambiguous in v2 (rebuttal vs synthesis) so
// callers that know the mode should prefer the named phase; without a name we
// keep v1 semantics (3 = synthesis).
const NUMERIC_TO_NAME = { 1: 'opinions', 2: 'review', 3: 'synthesis' };

/**
 * Normalize a phase label from an event payload. v2 carries the phase NAME in
 * `phase`; v1 carries a number. Also accepts a legacy `phase_name` for
 * forwards-compat with any intermediate backend shape.
 *
 * Numeric 3 always maps to `synthesis`: v1 callers never had a rebuttal phase,
 * and v2 rebuttal always arrives as the named string 'rebuttal'.
 *
 * @param {{phase?: number|string, phase_name?: string}} evt
 * @returns {'opinions'|'review'|'rebuttal'|'synthesis'|null}
 */
export function normalizePhase(evt) {
  const raw = evt?.phase;
  if (typeof raw === 'string') {
    const named = raw.trim();
    if (COUNCIL_PHASES.includes(named)) return named;
  }
  const legacyName = typeof evt?.phase_name === 'string' ? evt.phase_name.trim() : '';
  if (COUNCIL_PHASES.includes(legacyName)) return legacyName;
  const numeric = typeof raw === 'string' ? Number(raw) : raw;
  return NUMERIC_TO_NAME[numeric] || null;
}

/**
 * Normalize a terminal status. Unknown/absent settles as `error` so a run can
 * never be left in a running phase by a malformed terminal event.
 * @param {unknown} status
 * @returns {'complete'|'partial'|'cancelled'|'error'}
 */
export function normalizeStatus(status) {
  return TERMINAL_STATUSES.includes(status) ? status : 'error';
}

/**
 * The status implied by a stream that ended WITHOUT a server `council:done`.
 * Received output is preserved: if anything was received the run is `partial`,
 * otherwise it is an `error` (never a silent hang, never a fabricated success).
 * @param {boolean} hasContent
 * @returns {'partial'|'error'}
 */
export function statusOnEof(hasContent) {
  return hasContent ? 'partial' : 'error';
}

/**
 * Truthfully describe a terminal outcome. `status` is independent of phase: the
 * run may have finished at `review` and still be `partial`.
 *
 * @param {object} payload  council:done fields + locally observed state
 * @returns {{status, saved, costKnown, knownCostUsd, totalCostUsd, durationMs, effectiveChairman, deliberationId, cancelled}}
 */
export function normalizeTerminal(payload = {}) {
  const status = normalizeStatus(payload.status);
  // A disconnected client cannot confirm whether the server saved the run.
  const saved = typeof payload.saved === 'boolean' ? payload.saved : null;
  const costKnown = payload.cost_known === true;
  const knownCostUsd = typeof payload.known_cost_usd === 'number' ? payload.known_cost_usd : null;
  // total_cost_usd is only meaningful when cost is KNOWN. Unknown must NOT
  // coerce to 0 (CONTEXT: unknown != free).
  const totalCostUsd = costKnown && typeof payload.total_cost_usd === 'number'
    ? payload.total_cost_usd
    : null;
  return {
    status,
    saved,
    costKnown,
    knownCostUsd,
    totalCostUsd,
    durationMs: typeof payload.duration_ms === 'number' ? payload.duration_ms : null,
    effectiveChairman: payload.effective_chairman || null,
    deliberationId: payload.deliberation_id || null,
    cancelled: status === 'cancelled',
  };
}

/** Human label for a terminal status (pt-BR, matches the product voice). */
export function statusLabel(status) {
  // 'running' is written by the v2 persistStart header; it is NOT a failure.
  if (status === 'running') return 'Em andamento';
  switch (normalizeStatus(status)) {
    case 'complete': return 'Concluída';
    case 'partial': return 'Parcial';
    case 'cancelled': return 'Cancelada';
    default: return 'Falhou';
  }
}

/**
 * Truthful cost line. Unknown cost is never rendered as a currency value.
 * @param {{costKnown: boolean, knownCostUsd: number|null, totalCostUsd: number|null}} terminal
 * @returns {string}
 */
export function formatCost({ costKnown, knownCostUsd, totalCostUsd } = {}) {
  if (!costKnown) return 'Custo desconhecido';
  const value = typeof knownCostUsd === 'number' ? knownCostUsd
    : (typeof totalCostUsd === 'number' ? totalCostUsd : null);
  if (value == null) return 'Custo desconhecido';
  if (value === 0) return 'Sem custo (US$ 0,00)';
  return `US$ ${value.toFixed(4)}`;
}
